import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import LogisticsPageHero from "../Logistics/LogisticsPageHero";
import MultiChannelTrustSection from "./MultiChannelTrustSection";
import Industries from "../Homepage/Industries";
import "./Multichannelfulfilmentcontent.css";
import { useSectionInView, useStaggerReveal } from "../../hooks/useInViewOnce";

/* ════════════════════════════════════════════════
   ICONS
════════════════════════════════════════════════ */
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="10" fill="#28479e" opacity="0.12"/>
    <path d="M6 10L8.8 13L14 7" stroke="#28479e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const StorageIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="3" y="5" width="26" height="8" rx="2" fill="#28479e" opacity="0.12" stroke="#28479e" strokeWidth="1.8"/>
    <rect x="3" y="15" width="26" height="8" rx="2" fill="#28479e" opacity="0.12" stroke="#28479e" strokeWidth="1.8"/>
    <circle cx="8.5" cy="9" r="1.8" fill="#28479e"/>
    <circle cx="8.5" cy="19" r="1.8" fill="#28479e"/>
    <path d="M14 9H24M14 19H24" stroke="#28479e" strokeWidth="1.6" strokeLinecap="round" opacity="0.5"/>
    <path d="M3 26H29" stroke="#28479e" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const OrderIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="5" y="3" width="22" height="26" rx="3" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M10 11H22M10 16H18M10 21H14" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="23" cy="23" r="6" fill="#28479e"/>
    <path d="M20.5 23L22.3 24.8L25.5 21.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PackIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 3L28 9V23L16 29L4 23V9L16 3Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M16 3V29M4 9L16 15L28 9" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);
const ShipIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="3" y="11" width="20" height="13" rx="2" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M23 16H30L32 21V24H23V16Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.8" strokeLinejoin="round"/>
    <circle cx="10" cy="25.5" r="3" fill="white" stroke="#28479e" strokeWidth="1.8"/>
    <circle cx="27" cy="25.5" r="3" fill="white" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M3 16H23" stroke="#28479e" strokeWidth="1.7"/>
  </svg>
);
const ReturnIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="12" fill="#28479e" opacity="0.08" stroke="#28479e" strokeWidth="1.8"/>
    <path d="M10 16C10 12.69 12.69 10 16 10C18.5 10 20.67 11.5 21.66 13.67" stroke="#28479e" strokeWidth="2" strokeLinecap="round"/>
    <path d="M22 16C22 19.31 19.31 22 16 22C13.5 22 11.33 20.5 10.34 18.33" stroke="#28479e" strokeWidth="2" strokeLinecap="round"/>
    <path d="M21 10L22.5 14H18.5" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11 22L9.5 18H13.5" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const TrackIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="12" fill="#28479e" opacity="0.08" stroke="#28479e" strokeWidth="1.8"/>
    <circle cx="16" cy="16" r="4.5" fill="#28479e" opacity="0.18"/>
    <circle cx="16" cy="16" r="2" fill="#28479e"/>
    <path d="M16 4V8M16 24V28M4 16H8M24 16H28" stroke="#28479e" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);


const services = [
  {
    icon: <StorageIcon />,
    title: "Inventory Storage",
    desc: "One stock pool, live across every channel — the foundation of reliable ecommerce fulfilment from a single fulfilment centre UK. WMS updates on every receive, pick and return — so you spot shortages before overselling costs you a cancellation.",
  },
  {
    icon: <OrderIcon />,
    title: "Order Processing",
    desc: "Amazon, Shopify, eBay and TikTok Shop orders import automatically — no manual CSVs. Core ecommerce fulfillment services, routed to pick within minutes using channel-specific SLA rules.",
  },
  {
    icon: <PackIcon />,
    title: "Pick & Pack",
    desc: "99.9% accuracy rate. Barcode-verified on every order. If we get it wrong, we reship at our cost.",
  },
  {
    icon: <ShipIcon />,
    title: "Fast UK Shipping",
    desc: "Same-day dispatch cut-off available. Tracked labels from £1.99. Royal Mail, DPD and DHL — best carrier per order, not one-size-fits-all.",
  },
  {
    icon: <ReturnIcon />,
    title: "Returns Management",
    desc: "Returns scanned and graded within 24–48 hours. Sellable stock restocked fast, with SKU-level reporting by channel so you fix repeat issues.",
  },
  {
    icon: <TrackIcon />,
    title: "Real-Time Tracking",
    desc: "Tracking synced to every platform the moment labels print. Protects your marketplace metrics — no manual uploads or end-of-day batch files.",
  },
];



const benefits = [
  "Centralised inventory management across all channels",
  "Faster order dispatch — same-day cut-off available",
  "Reduced operational workload for your team",
  "Scalable ecommerce fulfilment that grows with your business",
  "Improved customer satisfaction and fewer complaints",
  "One dashboard for all platforms and all orders",
  "No warehouse lease or fulfilment staff to manage",
  "Real-time stock syncing prevents overselling",
];


/* ════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════ */
export default function MultiChannelFulfilment() {
  const svc  = useStaggerReveal(services.length, 0.1, 90);
  const ben  = useStaggerReveal(benefits.length, 0.1, 90);
  const { ref: pricRef, inView: pricIn } = useSectionInView(0.12);
  const { ref: priceMatchRef, inView: priceMatchIn } = useSectionInView(0.12);
//   const { ref: ctaRef,  inView: ctaIn  } = useInView();

  // Mobile slider ref for services section
  const servicesSliderRef = useRef<HTMLDivElement>(null);
  const servicesAutoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-scroll functionality for mobile services section
  useEffect(() => {
    const slider = servicesSliderRef.current;
    if (!slider) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let currentIndex = 0;
    const getCardWidth = () => {
      const containerWidth = slider.offsetWidth;
      return containerWidth * 0.85; // 85% width for each card
    };
    const gap = 16;

    const scrollToCard = (index: number) => {
      if (!slider) return;
      const cardWidth = getCardWidth();
      const scrollPosition = index * (cardWidth + gap);
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    };

    const autoScroll = () => {
      currentIndex = (currentIndex + 1) % services.length;
      scrollToCard(currentIndex);
    };

    // Start auto-scroll after initial delay
    const startAutoScroll = setTimeout(() => {
      servicesAutoScrollIntervalRef.current = setInterval(autoScroll, 4000); // Scroll every 4 seconds
    }, 2000);

    // Cleanup
    return () => {
      clearTimeout(startAutoScroll);
      if (servicesAutoScrollIntervalRef.current) {
        clearInterval(servicesAutoScrollIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="mcf-root">

      <LogisticsPageHero
        ariaLabel="Multi-channel fulfilment UK"
        imageSrc="/Images/smart-ecommerce-fulfillment-warehouse-uk-amazon-shopify-tiktok.webp"
        imageAlt="Multi-channel fulfilment UK warehouse — Amazon, Shopify, eBay, TikTok"
        titleLight="Multi-Channel Fulfilment UK —"
        titleAccent="Amazon, Shopify, eBay & TikTok Shop from One Centre"
        description={
          <p>
            <strong>What is multi-channel fulfilment?</strong> It is the process of managing orders from multiple sales platforms (like Amazon, Shopify, eBay, and TikTok Shop) through a single, centralised warehouse. Pick Pack Pro is a UK-based 3PL and multi-channel fulfilment centre operating from Milton Keynes. We provide 99.9% pick accuracy by syncing all your storefronts to one stock pool — handling storage, pick, pack, dispatch and returns seamlessly in one place.
          </p>
        }
        features={[
          'Picking Fees from £0.99',
          'Within 24 hours, items will be shipped from £1.99.',
          '99.9% Order Accuracy',
          'Dedicated Account Manager',
          'No long-term contracts',
        ]}
        ctaSlot={
          <div className="sh-cta-wrapper">
            <Link to="/contact-fulfillment-services" className="sh-cta-btn">
              Get a free quote
            </Link>
            <button
              type="button"
              className="logistics-hero-secondary-btn"
              onClick={() => {
                const el = document.getElementById('how-it-works');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              See how it works
            </button>
          </div>
        }
      />

      <MultiChannelTrustSection />

      {/* ══ PRICE MATCH PROMISE ═══════════════════════ */}
      <section
        className="mcf-section mcf-blue-section mcf-price-match"
        id="price-match-promise"
        aria-labelledby="mcf-price-match-title"
      >
        <div className="mcf-container">
          <div
            ref={priceMatchRef}
            className={`mcf-price-match-box ${priceMatchIn ? "mcf-in" : "mcf-out"}`}
          >
            <span className="mcf-tag mcf-tag-white-outline">Price Match Promise</span>
            <h2 id="mcf-price-match-title" className="mcf-h2 mcf-h2-white mcf-price-match-title">
              Our Price Match Promise — We Won&apos;t Be Beaten on Cost
            </h2>
            <ul className="mcf-price-match-list">
              <li>
                <CheckIcon />
                <span>
                  Found a cheaper like-for-like 3PL quote in the UK? Send us the breakdown — our{' '}
                  <strong>3PL price match UK</strong> policy means we match or beat comparable pick, pack and storage fees.
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>Affordable 3PL UK small business</strong> pricing from £0.99 per pick, with transparent quotes and no hidden setup charges or long lock-ins.
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  Whether you need <strong>cheap fulfilment UK</strong> or the <strong>cheapest 3PL UK</strong> for your order volume, we keep your costs competitive as you scale — not just on day one.
                </span>
              </li>
            </ul>
            <Link to="/contact-fulfillment-services" className="mcf-btn mcf-btn-white">
              Send us your quote to match <ArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ════════════════════════════════ */}
      <section className="mcf-section mcf-white">
        <div className="mcf-container">
          <div className="mcf-sec-head">
            <span className="mcf-tag">Services</span>
            <h2 className="mcf-h2">Our UK Pick and Pack Services: Every Channel, One System</h2>
            <p className="mcf-sec-p">
              Our UK pick-and-pack services provide everything growing ecommerce brands need to store, fulfil, and deliver orders efficiently. As a trusted fulfilment centre UK partner, we manage inventory, order processing, shipping, and returns through one streamlined system. Inventory stays synchronised across Amazon, Shopify, eBay, TikTok Shop, and your website — read our{' '}
              <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment UK explained</Link> guide for costs and workflow detail, or compare{' '}
              <Link to="/blog/sfp-vs-fba-2026">SFP vs FBA</Link> if you split Amazon fulfilment models.
            </p>
          </div>
          
          {/* Desktop Grid */}
          <div className="mcf-services-grid">
            {services.map((s, i) => (
              <div
                key={i}
                ref={el => { svc.refs.current[i] = el; }}
                className={`mcf-svc-card ${svc.visible[i] ? "mcf-in" : "mcf-out"}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="mcf-svc-icon">{s.icon}</div>
                <div>
                  <h3 className="mcf-svc-title">{s.title}</h3>
                  <p className="mcf-svc-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="mcf-services-mobile-slider" ref={servicesSliderRef}>
            {services.map((s, i) => (
              <div key={i} className="mcf-services-mobile-slide">
                <div className="mcf-services-mobile-card">
                  <div className="mcf-svc-icon">{s.icon}</div>
                  <div>
                    <p className="mcf-svc-title">{s.title}</p>
                    <p className="mcf-svc-desc">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Industries />

      {/* ══ BENEFITS ════════════════════════════════ */}
      <section className="mcf-section mcf-blue-section">
        <div className="mcf-container">
          <div className="mcf-benefits-layout">
            <div className="mcf-benefits-left">
              <span className="mcf-tag mcf-tag-white-outline">Why Choose Us</span>
              <h2 className="mcf-h2 mcf-h2-white">Key Benefits of Multi-Channel Fulfilment for UK eCommerce Sellers</h2>
              <p style={{ color: "rgba(255,255,255,.72)", fontSize: "1rem", lineHeight: 1.72, marginBottom: 0 }}>
                One fulfilment centre UK delivering ecommerce fulfilment, e-commerce fulfilment, and ecommerce fulfillment UK solutions with the speed, visibility, and flexibility growing businesses need.
              </p>
            </div>
            <div className="mcf-benefits-right">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  ref={el => { ben.refs.current[i] = el; }}
                  className={`mcf-benefit-chip ${ben.visible[i] ? "mcf-in" : "mcf-out"}`}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <CheckIcon />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      

      {/* ══ PRICING ═════════════════════════════════ */}
      <section className="mcf-section mcf-off">
        <div className="mcf-container">
          <div
            className={`mcf-pricing-card ${pricIn ? "mcf-in" : "mcf-out"}`}
            ref={pricRef}
          >
            <div className="mcf-pricing-left">
              {/* <span className="mcf-tag">Pricing</span> */}
              <h2 className="mcf-h2">Get a Free Multi-Channel Fulfilment Quote</h2>
              <p className="mcf-body">
                Tell us your order volume, storage requirements, and sales channels, and we&apos;ll create tailored ecommerce fulfilment services UK — built around your business whether you need ecommerce fulfilment, ecommerce fulfillment services, or a single operator for every marketplace.
              </p>
              <ul className="mcf-pricing-list">
                {["No hidden fees", "Flexible monthly plans", "Scales with your order volume", "Cancel anytime"].map((item, i) => (
                  <li key={i}><CheckIcon /><span>{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="mcf-pricing-right">
              <div className="mcf-pricing-cta-box">
                <h3 className="mcf-pricing-cta-title">Request Your Quote</h3>
                <p className="mcf-pricing-cta-sub">Based on your order volume and storage requirements.</p>
                <Link to="/contact-fulfillment-services" className="mcf-btn mcf-btn-solid mcf-btn-full">
                  Get a free quote <ArrowRight />
                </Link>
                <Link to="/book-a-free-call" className="mcf-btn mcf-btn-ghost mcf-btn-full" style={{ marginTop: "12px" }}>
                  Book a discovery call
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

     
   
    </div>
  );
}
